import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, MapPin, Users, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toast as toastify } from "react-toastify";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateEventModal({ isOpen, onClose, onEventCreated, initialDate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "meeting",
    start_date: initialDate ? initialDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    start_time: "09:00",
    end_date: initialDate ? initialDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    end_time: "10:00",
    location: "",
    attendees: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!formData.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }

    // Combine date and time
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);

    if (endDateTime <= startDateTime) {
      toast.error("La hora de fin debe ser posterior a la hora de inicio");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      
      // Parse attendees
      const attendeesList = formData.attendees
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      const payload = {
        title: formData.title,
        description: formData.description || null,
        event_type: formData.event_type,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        location: formData.location || null,
        attendees: attendeesList,
        meeting_id: null
      };

      await axios.post(`${API}/calendar/events`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success("Evento creado exitosamente. Recibirás un correo de confirmación.");
      
      // Notificación push más prominente con react-toastify
      toastify.success(`🎉 ¡Evento creado! "${formData.title}" ha sido agregado al calendario`, {
        position: "top-right",
        autoClose: 5000,
      });
      
      onEventCreated();
      onClose();
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        event_type: "meeting",
        start_date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        end_date: new Date().toISOString().split('T')[0],
        end_time: "10:00",
        location: "",
        attendees: ""
      });

    } catch (error) {
      console.error("Error creating event:", error);
      toast.error(error.response?.data?.detail || "Error al crear evento");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="text-orange-500" />
            Crear Nuevo Evento
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Título */}
          <div>
            <Label htmlFor="title" className="text-sm font-medium">
              Título del evento *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Ej: Reunión de equipo"
              className="mt-1"
              required
            />
          </div>

          {/* Tipo de evento */}
          <div>
            <Label htmlFor="event_type" className="text-sm font-medium">
              Tipo de evento
            </Label>
            <Select value={formData.event_type} onValueChange={(value) => handleChange('event_type', value)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meeting">
                  <div className="flex items-center gap-2">
                    <Users size={16} />
                    Reunión
                  </div>
                </SelectItem>
                <SelectItem value="reminder">
                  <div className="flex items-center gap-2">
                    <Clock size={16} />
                    Recordatorio
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Fecha y hora de inicio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date" className="text-sm font-medium">
                Fecha de inicio *
              </Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="start_time" className="text-sm font-medium">
                Hora de inicio *
              </Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                className="mt-1"
                required
              />
            </div>
          </div>

          {/* Fecha y hora de fin */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="end_date" className="text-sm font-medium">
                Fecha de fin *
              </Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="end_time" className="text-sm font-medium">
                Hora de fin *
              </Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                className="mt-1"
                required
              />
            </div>
          </div>

          {/* Ubicación */}
          <div>
            <Label htmlFor="location" className="text-sm font-medium flex items-center gap-2">
              <MapPin size={16} />
              Ubicación (opcional)
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="Ej: Sala de juntas, Zoom, etc."
              className="mt-1"
            />
          </div>

          {/* Descripción */}
          <div>
            <Label htmlFor="description" className="text-sm font-medium flex items-center gap-2">
              <FileText size={16} />
              Descripción (opcional)
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detalles del evento..."
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Asistentes */}
          <div>
            <Label htmlFor="attendees" className="text-sm font-medium flex items-center gap-2">
              <Users size={16} />
              Asistentes (opcional)
            </Label>
            <Input
              id="attendees"
              value={formData.attendees}
              onChange={(e) => handleChange('attendees', e.target.value)}
              placeholder="Nombres separados por comas"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Ej: Juan Pérez, María López, Carlos Ruiz
            </p>
          </div>

          {/* Información de recordatorio */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-sm text-orange-800">
              📧 Recibirás un correo de confirmación al crear el evento y un recordatorio 1 hora antes del inicio.
            </p>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creando...
                </>
              ) : (
                "Crear Evento"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
